import React from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Order } from "../types";
import { useExchangeRates } from "../context/ExchangeRateContext";

interface KanbanCardProps {
  order: Order;
  formatDate: (date?: string) => string;
  workshopDuration?: string;
  onEdit?: (orderId: string) => void;
  onDetail?: (orderId: string) => void;
  /** Transfer yetkisi yoksa sürükleme kapatılır — kullanıcı boşa deneyip 403 almasın. */
  dragDisabled?: boolean;
}

const AVATAR_COLORS = [
  "#1B2A4A",
  "#0e7490",
  "#7c3aed",
  "#b45309",
  "#0f766e",
  "#be185d",
  "#16233F",
];
const pickColor = (seed: string) => {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
};

const KanbanCard: React.FC<KanbanCardProps> = ({
  order,
  formatDate,
  workshopDuration,
  onEdit,
  onDetail,
  dragDisabled = false,
}) => {
  const { usdRate, eurRate } = useExchangeRates();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: order.orderId, disabled: dragDisabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    // Yetki yoksa imleç de "tutulabilir" demesin
    cursor: dragDisabled ? "default" : undefined,
  };

  // Kartın tamamı tutamaç; aksiyon butonları sürüklemeyi başlatmasın
  const stopDrag = (e: React.SyntheticEvent) => e.stopPropagation();

  const getPriorityClass = (priority?: string) => {
    if (!priority) return "low";
    if (priority === "Yüksek" || priority === "High") return "high";
    if (priority === "Orta" || priority === "Medium") return "medium";
    return "low";
  };
  const prio = getPriorityClass(order.priority);

  const calculateDuration = () => {
    const start = new Date(order.acceptanceDate);
    const now = new Date();
    const hours = Math.floor(
      (now.getTime() - start.getTime()) / (1000 * 60 * 60),
    );
    const days = Math.floor(hours / 24);
    if (days > 0) return `${days}g`;
    return `${hours}s`;
  };

  // Termin aciliyeti
  const deadlineState = (): "overdue" | "soon" | "normal" | null => {
    if (!order.deadline) return null;
    const d = new Date(order.deadline);
    const now = new Date();
    const days = Math.ceil((d.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    if (days < 0) return "overdue";
    if (days <= 3) return "soon";
    return "normal";
  };
  const dl = deadlineState();

  const formatPrice = () => {
    const total = (order.price || 0) * order.quantity;
    const currency = order.priceCurrency || order.currency;
    let displayAmount = total;
    let displayCurrency = currency;
    if (currency === "USD" && usdRate) {
      displayAmount = total * usdRate;
      displayCurrency = "TRY";
    } else if (currency === "EUR" && eurRate) {
      displayAmount = total * eurRate;
      displayCurrency = "TRY";
    }
    const symbol =
      displayCurrency === "USD" ? "$" : displayCurrency === "EUR" ? "€" : "₺";
    return `${symbol}${displayAmount.toLocaleString("tr-TR", { maximumFractionDigits: 0 })}`;
  };

  const opInitials = order.operator
    ? `${order.operator.firstName?.charAt(0) || ""}${order.operator.lastName?.charAt(0) || ""}`.toUpperCase()
    : "";
  const isLongInWorkshop = !!workshopDuration && workshopDuration.includes("gün");

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`kcard kcard--prio-${prio} ${isDragging ? "kcard--dragging" : ""}`}
      {...attributes}
      {...listeners}
    >
      {/* Hover aksiyonları */}
      <div className="kcard__actions">
        <button
          className="kcard__btn kcard__btn--view"
          onPointerDown={stopDrag}
          onMouseDown={stopDrag}
          onTouchStart={stopDrag}
          onKeyDown={stopDrag}
          onClick={(e) => {
            e.stopPropagation();
            onDetail?.(order.orderId);
          }}
          title="Detay"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        </button>
        <button
          className="kcard__btn kcard__btn--edit"
          onPointerDown={stopDrag}
          onMouseDown={stopDrag}
          onTouchStart={stopDrag}
          onKeyDown={stopDrag}
          onClick={(e) => {
            e.stopPropagation();
            onEdit?.(order.orderId);
          }}
          title="Düzenle"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
          </svg>
        </button>
      </div>

      {/* Sürüklenebilir gövde */}
      <div className="kcard__body">
        <div className="kcard__top">
          <span className="kcard__model">{order.model?.modelCode || "-"}</span>
          {order.priority && (
            <span className={`kcard__priority kcard__priority--${prio}`}>
              {order.priority}
            </span>
          )}
        </div>

        <div className="kcard__firm">{order.firm?.firmName || "—"}</div>

        <div className="kcard__meta">
          <span className="kcard__metaitem">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
              <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
              <line x1="12" y1="22.08" x2="12" y2="12" />
            </svg>
            {order.quantity} ad
          </span>
          <span className="kcard__metaitem kcard__price">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="1" x2="12" y2="23" />
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
            </svg>
            {formatPrice()}
          </span>
        </div>

        <div className="kcard__footer">
          {order.operator && (
            <span
              className="kcard__avatar"
              style={{ background: pickColor(order.operatorId || opInitials) }}
              title={`${order.operator.firstName} ${order.operator.lastName}`}
            >
              {opInitials}
            </span>
          )}
          {order.deadline && (
            <span
              className={`kcard__chip ${dl === "overdue" ? "is-overdue" : dl === "soon" ? "is-soon" : ""}`}
              title="Termin"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              {formatDate(order.deadline)}
            </span>
          )}
          <span className="kcard__chip" title="Sistemdeki süre">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            {calculateDuration()}
          </span>
          {workshopDuration && (
            <span
              className={`kcard__chip ${isLongInWorkshop ? "is-long" : ""}`}
              title="Bu atölyede geçen süre"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M2 20h20M4 20V8l6 4V8l6 4V8l4 2v10" />
              </svg>
              {workshopDuration}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default KanbanCard;
